import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  EllipsisSpan,
  Form,
  FormTitle,
  ShowInput,
  SubContent,
  Table,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Space } from 'antd';
import {
  COMPONENT_MODE,
  COMPONENT_MODE_LABELS,
  ComponentMode,
  FIELD_TYPE_OPTIONS,
  FIELDS,
} from '@/pages/AssetInformationManagement/constant';
import { ModalForm } from '@/pages/AssetInformationManagement/CreateOrEditTemplate/ModalForm';
import { apiV2AssetTemplateDetailPost } from '@maxtropy/device-customer-apis-v2';
import { getCascaderLabelById } from '@/pages/AssetInformationManagement/utils';
import { useDeviceTypeCascaderOptions } from '@/pages/AssetInformationManagement/hooks';
import { v4 as uuid } from 'uuid';

interface Props {
  mode: ComponentMode;
}

const columns = [
  {
    title: '字段名称',
    dataIndex: FIELDS.FIELD_NAME,
    width: 180,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '字段类型',
    dataIndex: FIELDS.FIELD_TYPE,
    width: 180,
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={FIELD_TYPE_OPTIONS.find(i => i.value === v)?.label || '-'} />,
  },
];

const DetailTemplate: React.FC<Props> = ({ mode }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldDataList, setFieldDataList] = useState<any[]>([]);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [edit, setEdit] = useState<{ index: number; mode: ComponentMode; value: object }>({
    index: 0,
    mode: COMPONENT_MODE.CREATE,
    value: {},
  });

  const routesTitle = useMemo(() => ({ name: `${COMPONENT_MODE_LABELS[mode]}模版` }), [mode]);

  const getFormInitialValues = useCallback(() => {
    return {
      [FIELDS.TEMPLATE_NAME]: '',
      [FIELDS.DEVICE_NAME]: '',
    };
  }, []);

  const cascaderOptions = useDeviceTypeCascaderOptions();

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiV2AssetTemplateDetailPost({ id });
      console.log('/detail的resp', resp);
      // 手动给enumlist加id
      // 后端返回string[], 在fetch和submit的时候改成string[], 中途用object[]
      resp?.[FIELDS.FIELD_DATA_LIST]?.forEach((item: any) => {
        const enumList = item[FIELDS.ENUM_ITEM_LIST];
        if (enumList && enumList.length) {
          const arr = enumList.map((name: any) => {
            return {
              // id: crypto.randomUUID(),
              id: uuid(),
              name,
            };
          });
          (item as any)[FIELDS.ENUM_ITEM_LIST] = arr;
        }
      });
      form.setFieldsValue({
        [FIELDS.TEMPLATE_NAME]: resp[FIELDS.TEMPLATE_NAME],
        [FIELDS.DEVICE_NAME]: getCascaderLabelById(cascaderOptions, resp[FIELDS.DEVICE_TYPE] || ''),
      });
      setFieldDataList(resp[FIELDS.FIELD_DATA_LIST] || []);
    } finally {
      setLoading(false);
    }
  }, [cascaderOptions, form, id]);

  // 数据初始化
  useEffect(() => {
    if (mode !== COMPONENT_MODE.CREATE) {
      fetchDetail();
    }
  }, [fetchDetail, mode]);

  const lastColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 120,
      fixed: 'right' as 'right',
      render: (_: any, record: any, index: number) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                setVisibleModal(true);
                setEdit({ index, mode: COMPONENT_MODE.VIEW, value: { ...record } });
              }}
            >
              查看
            </Button>
          </Space>
        );
      },
    },
  ];

  const cancel = useCallback(() => {
    form.resetFields();
    setFieldDataList([]);
    setLoading(false);
    navigate(`/asset/equipment/assetInfo`);
  }, [form, navigate]);

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), routesTitle]}
      // className={styles.wrapper}
      isLoading={loading}
    >
      <FormTitle title={routesTitle.name} />
      <SubContent className={'mb-8'}>
        <Form
          initialValues={getFormInitialValues()}
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 8 }}
        >
          <Form.Item
            label="模版名称"
            // layout="horizontal"
            name={FIELDS.TEMPLATE_NAME}
          >
            <ShowInput />
          </Form.Item>

          <Form.Item label="设备类型" name={[FIELDS.DEVICE_NAME]}>
            <ShowInput />
          </Form.Item>
        </Form>
      </SubContent>

      <SubContent title={'字段配置'} className={'mb-8'}>
        <Row>
          <Col span={12}>
            <Table columns={lastColumns} dataSource={fieldDataList} />
          </Col>
        </Row>

        <Space
          size={8}
          style={{ marginTop: 24 }}
          // className="sticky-footer"
        >
          <Button onClick={cancel}>返回</Button>
        </Space>
      </SubContent>

      {/* 弹窗 */}
      <ModalForm
        mode={COMPONENT_MODE.VIEW}
        fieldNames={fieldDataList.map(item => item[FIELDS.FIELD_NAME])}
        visible={visibleModal}
        setVisible={setVisibleModal}
        onFinish={() => {}}
        echoValue={edit.value}
      />
    </Wrapper>
  );
};

export default DetailTemplate;
